<template>
  <div class="hist-logs">
    <APageHead
      title="이력 로그"
      sub="환불 · 클래스 삭제 · 수강생 삭제 · 학습활동 삭제 · 바우처 기록을 조회합니다. 감사 기록이라 삭제할 수 없습니다."
    >
      <template #actions>
        <ABtn kind="ghost" icon="refresh" @click="fetchCurrent">새로고침</ABtn>
      </template>
    </APageHead>

    <div class="p-tabs">
      <button
        v-for="tab in TABS"
        :key="tab.id"
        type="button"
        :class="['p-tabs__tab', { 'is-active': activeTab === tab.id }]"
        @click="switchTab(tab.id)"
      >
        <i class="material-icons-round hist-logs__tab-icon">{{ tab.icon }}</i>
        {{ tab.label }}
        <span v-if="state[tab.id].loaded" class="p-tabs__count">{{ state[tab.id].total }}</span>
      </button>
    </div>

    <!-- 검색 -->
    <ACard overflow="visible">
      <div class="hist-logs__filters">
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">event</i>
            시작일 (KST)
          </label>
          <input v-model="current.filters.startDate" class="p-input" type="date" />
        </div>
        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">event_available</i>
            종료일 (KST)
          </label>
          <input v-model="current.filters.endDate" class="p-input" type="date" />
        </div>
        <div class="p-field hist-logs__filter--wide">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">search</i>
            검색어
          </label>
          <input
            v-model.trim="current.filters.keyword"
            class="p-input"
            type="text"
            maxlength="100"
            :placeholder="activeMeta.keywordHint"
            @keyup.enter="search"
          />
        </div>

        <!-- 환불 전용 -->
        <template v-if="activeTab === 'refunds'">
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">credit_card</i>
              결제수단
            </label>
            <select v-model="current.filters.paymentMethod" class="p-input">
              <option value="">전체</option>
              <option v-for="m in PAYMENT_METHODS" :key="m.value" :value="m.value">{{ m.label }}</option>
            </select>
          </div>
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">person_search</i>
              실행 주체
            </label>
            <select v-model="current.filters.actorType" class="p-input">
              <option value="all">전체</option>
              <option value="admin">관리자 실행</option>
              <option value="system">시스템 · 스토어</option>
            </select>
          </div>
        </template>

        <!-- 삭제 이력 전용 -->
        <div v-else-if="activeTab !== 'vouchers'" class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">meeting_room</i>
            클래스 번호
          </label>
          <input
            v-model.trim="current.filters.classroomIdx"
            class="p-input"
            type="text"
            inputmode="numeric"
            placeholder="예: 12"
            @keyup.enter="search"
          />
        </div>

        <!-- 학습활동 삭제 전용 (docs/학습활동_기록삭제.md §3). 클래스 번호는 위 삭제 이력 공통 칸을 씁니다 -->
        <template v-if="activeTab === 'learningActivityDeletions'">
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">badge</i>
              학생 회원 번호
            </label>
            <input v-model.trim="current.filters.studentIdx" class="p-input" type="text" inputmode="numeric" placeholder="users_idx" @keyup.enter="search" />
          </div>
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">qr_code</i>
              교재 바코드
            </label>
            <input v-model.trim="current.filters.barcode" class="p-input" type="text" placeholder="예: 9788952131171" @keyup.enter="search" />
          </div>
        </template>

        <!-- 바우처 전용 (docs/바우처_기관발행_관리자화면_가이드.md §5) -->
        <template v-if="activeTab === 'vouchers'">
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">apartment</i>
              기관 번호
            </label>
            <input v-model.trim="current.filters.organizationIdx" class="p-input" type="text" inputmode="numeric" placeholder="예: 3" @keyup.enter="search" />
          </div>
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">label</i>
              이벤트
            </label>
            <select v-model="current.filters.event" class="p-input" @change="search">
              <option value="">전체</option>
              <option v-for="e in VOUCHER_EVENTS" :key="e.value" :value="e.value">{{ e.label }}</option>
            </select>
          </div>
          <div class="p-field">
            <label class="p-field__label">
              <i class="material-icons-round p-field__label-icon">badge</i>
              멤버 회원 번호
            </label>
            <input v-model.trim="current.filters.memberUsersIdx" class="p-input" type="text" inputmode="numeric" placeholder="users_idx" @keyup.enter="search" />
          </div>
        </template>

        <div class="p-field">
          <label class="p-field__label">
            <i class="material-icons-round p-field__label-icon">format_list_numbered</i>
            표시 개수
          </label>
          <select v-model.number="current.pageSize" class="p-input" @change="search">
            <option :value="20">20개</option>
            <option :value="50">50개</option>
            <option :value="100">100개</option>
          </select>
        </div>

        <div class="hist-logs__filter-actions">
          <ABtn kind="primary" icon="search" @click="search">조회</ABtn>
          <ABtn kind="ghost" icon="refresh" @click="resetFilters">초기화</ABtn>
        </div>
      </div>
    </ACard>

    <!-- 결과 -->
    <ACard>
      <template #title>
        <span class="hist-logs__sec-title">
          <i class="material-icons-round hist-logs__sec-icon">{{ activeMeta.icon }}</i>
          {{ activeMeta.label }}
        </span>
      </template>
      <template #action>
        <APill kind="info">{{ current.total.toLocaleString() }}건</APill>
      </template>

      <div v-if="current.loading" class="hist-logs__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>이력을 불러오는 중...</p>
      </div>

      <AEmpty
        v-else-if="!current.items.length"
        title="조회 결과가 없습니다"
        sub="기간을 넓히거나 검색어를 지우고 다시 조회해 보세요."
      />

      <template v-else>
        <!-- 환불 이력 -->
        <ATable v-if="activeTab === 'refunds'" :columns="refundCols" :rows="current.items" row-key="idx">
          <template #cell:no="{ index }">
            <span class="hist-logs__num">{{ rowNo(index) }}</span>
          </template>
          <template #cell:refunded_at="{ row }">
            <span class="hist-logs__num">{{ fmtKst(row.refunded_at) }}</span>
          </template>
          <template #cell:payment="{ row }">
            <div class="hist-logs__stack">
              <div class="hist-logs__stack-top">
                <APill kind="default">{{ paymentMethodLabel(row.payment_method) }}</APill>
                <APill v-if="row.is_test" kind="warn">테스트</APill>
              </div>
              <span class="hist-logs__num hist-logs__sub" :title="`payment_idx ${row.payment_idx}`">
                {{ row.pay_code || '-' }}
              </span>
            </div>
          </template>
          <template #cell:textbook="{ row }">
            <span :title="row.textbook?.title">{{ row.textbook?.title || '-' }}</span>
          </template>
          <template #cell:learner="{ row }">
            <div class="hist-logs__stack">
              <span class="hist-logs__strong">{{ row.learner?.name || '-' }}</span>
              <span class="hist-logs__sub">{{ row.learner?.email || '' }}</span>
            </div>
          </template>
          <template #cell:actor="{ row }">
            <div class="hist-logs__stack">
              <template v-if="row.refunded_by">
                <span class="hist-logs__strong">{{ row.refunded_by.name || '-' }}</span>
                <span class="hist-logs__sub">{{ row.refunded_by.email || '' }}</span>
              </template>
              <APill v-else kind="dark">시스템 · 스토어</APill>
              <span class="hist-logs__sub">{{ refundSourceLabel(row.source) }}</span>
            </div>
          </template>
          <template #cell:reason="{ row }">
            <!-- 사유는 관리자 경로에만 있습니다. 빈 값이 오류로 보이지 않게 무채색 대시로 둡니다. -->
            <span v-if="row.reason" class="hist-logs__reason" :title="row.reason">{{ row.reason }}</span>
            <span v-else class="hist-logs__dash">—</span>
          </template>
          <template #cell:amount="{ row }">
            <div class="hist-logs__stack hist-logs__stack--end">
              <!--
                LS 는 판매 대행자라 구매자 국가의 세금을 가격 위에 더해 받고 환불 때도 함께 돌려줍니다.
                우리가 남기는 금액은 세전(결제 기록 금액) 기준이라 구매자가 실제로 받은 금액과 다를 수
                있습니다(docs/환불_즉시반영_관리자화면_가이드.md §3.3). 숫자만 두면 CS 에서 이 값을 실환불액으로 읽게 됩니다.
              -->
              <span
                class="hist-logs__strong hist-logs__num"
                :title="refundAmountTitle(row)"
              >
                {{ money(row.refund_amount, row.refund_currency_code || row.currency_code) }}
              </span>
              <!--
                툴팁만 두면 마우스를 올릴 이유가 없어 아무도 읽지 않습니다. 눈에 보이는 표시를
                함께 둬야 "이 숫자는 실환불액이 아니다"가 전달됩니다. L 행에만 붙입니다 —
                전 행에 붙이면 상시 문구가 되어 정작 LS 건에서 눈에 띄지 않습니다.
              -->
              <span
                v-if="row.payment_method === 'L'"
                class="hist-logs__sub"
                :title="refundAmountTitle(row)"
              >세전 기준 · 실환불액은 LS</span>
              <APill v-if="row.is_full_refund === false" kind="warn">부분 환불</APill>
              <span v-if="row.is_full_refund === false" class="hist-logs__sub hist-logs__num">
                결제 {{ money(row.amount, row.currency_code) }}
              </span>
            </div>
          </template>
          <template #cell:plans="{ row }">
            <div class="hist-logs__stack hist-logs__stack--center">
              <span class="hist-logs__num">회수 {{ row.expired_plan_count ?? 0 }}</span>
              <span v-if="row.restored_plan_count" class="hist-logs__num hist-logs__sub">
                복구 {{ row.restored_plan_count }}
              </span>
            </div>
          </template>
        </ATable>

        <!-- 클래스 삭제 이력 -->
        <ATable
          v-else-if="activeTab === 'classroomDeletions'"
          :columns="classroomCols"
          :rows="current.items"
          row-key="idx"
        >
          <template #cell:no="{ index }">
            <span class="hist-logs__num">{{ rowNo(index) }}</span>
          </template>
          <template #cell:deleted_at="{ row }">
            <span class="hist-logs__num">{{ fmtKst(row.deleted_at) }}</span>
          </template>
          <template #cell:classroom="{ row }">
            <div class="hist-logs__stack">
              <span class="hist-logs__strong">{{ row.classroom_name || '-' }}</span>
              <span class="hist-logs__sub hist-logs__num">#{{ row.classroom_idx }}</span>
            </div>
          </template>
          <template #cell:instructor="{ row }">
            <div class="hist-logs__stack">
              <span>{{ row.instructor?.name || '-' }}</span>
              <span class="hist-logs__sub">{{ row.instructor?.email || '' }}</span>
            </div>
          </template>
          <template #cell:deleted_by="{ row }">
            <div class="hist-logs__stack">
              <div class="hist-logs__stack-top">
                <span class="hist-logs__strong">{{ row.deleted_by?.name || '-' }}</span>
                <APill :kind="actorKind(row.deleted_by)">{{ actorLabel(row.deleted_by) }}</APill>
              </div>
              <span class="hist-logs__sub">{{ row.deleted_by?.email || '' }}</span>
            </div>
          </template>
          <template #cell:enrollments="{ row }">
            <ABtn
              v-if="row.enrollment_count"
              kind="ghost"
              size="sm"
              icon="group"
              @click="openEnrollments(row)"
            >{{ row.enrollment_count }}명</ABtn>
            <span v-else class="hist-logs__dash">0명</span>
          </template>
          <template #cell:deleted_counts="{ row }">
            <div class="hist-logs__chips">
              <span v-for="c in deletedCountChips(row.deleted_counts)" :key="c.key" class="hist-logs__chip">
                {{ c.label }} <b>{{ c.value }}</b>
              </span>
              <span v-if="!deletedCountChips(row.deleted_counts).length" class="hist-logs__dash">—</span>
            </div>
          </template>
          <template #cell:actions="{ row }">
            <ABtn
              kind="ghost"
              size="sm"
              icon="person_remove"
              icon-only
              title="이 클래스의 수강생 삭제 이력 보기"
              @click="showEnrollmentDeletions(row.classroom_idx)"
            />
          </template>
        </ATable>

        <!-- 수강생 삭제 이력 -->
        <ATable v-else-if="activeTab === 'enrollmentDeletions'" :columns="enrollmentCols" :rows="current.items" row-key="idx">
          <template #cell:no="{ index }">
            <span class="hist-logs__num">{{ rowNo(index) }}</span>
          </template>
          <template #cell:deleted_at="{ row }">
            <span class="hist-logs__num">{{ fmtKst(row.deleted_at) }}</span>
          </template>
          <template #cell:classroom="{ row }">
            <div class="hist-logs__stack">
              <span class="hist-logs__strong">{{ row.classroom_name || '-' }}</span>
              <span class="hist-logs__sub hist-logs__num">#{{ row.classroom_idx }}</span>
            </div>
          </template>
          <template #cell:student="{ row }">
            <div class="hist-logs__stack">
              <span class="hist-logs__strong">{{ row.student?.name || '-' }}</span>
              <span class="hist-logs__sub">{{ row.student?.email || '' }}</span>
            </div>
          </template>
          <template #cell:instructor="{ row }">
            <div class="hist-logs__stack">
              <span>{{ row.instructor?.name || '-' }}</span>
              <span class="hist-logs__sub">{{ row.instructor?.email || '' }}</span>
            </div>
          </template>
          <template #cell:status_before="{ row }">
            <APill :kind="statusKind(row.status_before)">{{ statusLabel(row.status_before) }}</APill>
          </template>
          <template #cell:enrollment_date="{ row }">
            <span class="hist-logs__num">{{ row.enrollment_date || '-' }}</span>
          </template>
          <template #cell:deleted_by="{ row }">
            <div class="hist-logs__stack">
              <div class="hist-logs__stack-top">
                <span class="hist-logs__strong">{{ row.deleted_by?.name || '-' }}</span>
                <APill :kind="actorKind(row.deleted_by)">{{ actorLabel(row.deleted_by) }}</APill>
              </div>
              <span class="hist-logs__sub">{{ row.deleted_by?.email || '' }}</span>
            </div>
          </template>
        </ATable>

        <!--
          학습활동(풀이 기록) 삭제 이력 (GET /system/logs/history/learning-activity-deletions, docs/학습활동_기록삭제.md §3).
          완전 삭제라 이 이력이 지운 기록의 유일한 흔적입니다. 지운 답안 자체는 내려오지 않습니다.
        -->
        <ATable
          v-else-if="activeTab === 'learningActivityDeletions'"
          :columns="activityCols"
          :rows="current.items"
          row-key="idx"
        >
          <template #cell:no="{ index }">
            <span class="hist-logs__num">{{ rowNo(index) }}</span>
          </template>
          <template #cell:deleted_at="{ row }">
            <span class="hist-logs__num">{{ fmtKst(row.deleted_at) }}</span>
          </template>
          <template #cell:student="{ row }">
            <div class="hist-logs__stack">
              <!-- 학생 행이 없는 기록은 이름 · 이메일이 비어 옵니다 -->
              <span class="hist-logs__strong">{{ row.student?.name || '-' }}</span>
              <span class="hist-logs__sub">{{ row.student?.email || '' }}</span>
              <span v-if="row.student?.idx" class="hist-logs__sub hist-logs__num">#{{ row.student.idx }}</span>
            </div>
          </template>
          <template #cell:classrooms="{ row }">
            <!-- 삭제 시점에 그 학생이 승인 수강 중이고 그 교재가 등록된 클래스 전부. 0개일 수도, 여러 개일 수도 있습니다 -->
            <div v-if="(row.classrooms || []).length" class="hist-logs__stack">
              <span v-for="c in row.classrooms" :key="c.idx">
                {{ c.name || '-' }}
                <span class="hist-logs__sub hist-logs__num">#{{ c.idx }}</span>
              </span>
            </div>
            <span v-else class="hist-logs__dash" title="삭제 시점에 수강 중인 클래스가 없었습니다">—</span>
          </template>
          <template #cell:textbook="{ row }">
            <div class="hist-logs__stack">
              <span :title="row.textbook?.title">{{ row.textbook?.title || '-' }}</span>
              <span class="hist-logs__sub hist-logs__num">{{ row.textbook?.barcode || '' }}</span>
            </div>
          </template>
          <template #cell:activity="{ row }">
            <div class="hist-logs__stack">
              <span class="hist-logs__strong" :title="activityLesson(row)">{{ activityLesson(row) }}</span>
              <div class="hist-logs__stack-top">
                <APill kind="default">{{ activityTypeLabel(row) }}</APill>
                <span v-if="activityScore(row)" class="hist-logs__num">{{ activityScore(row) }}</span>
                <APill v-if="row.has_teacher_feedback" kind="warn">강사 피드백</APill>
                <APill v-if="row.audio_dirs_removed" kind="err">녹음 {{ row.audio_dirs_removed }}</APill>
              </div>
              <span class="hist-logs__sub hist-logs__num" :title="row.activity?.dataId || null">
                학습 {{ activityTime(row.activity?.completed_at) }}
              </span>
            </div>
          </template>
          <template #cell:reason="{ row }">
            <span v-if="row.reason" class="hist-logs__reason" :title="row.reason">{{ row.reason }}</span>
            <span v-else class="hist-logs__dash">—</span>
          </template>
          <template #cell:deleted_by="{ row }">
            <div class="hist-logs__stack">
              <div class="hist-logs__stack-top">
                <span class="hist-logs__strong">{{ row.deleted_by?.name || '-' }}</span>
                <APill :kind="actorKind(row.deleted_by)">{{ actorLabel(row.deleted_by) }}</APill>
              </div>
              <span class="hist-logs__sub">{{ row.deleted_by?.email || '' }}</span>
            </div>
          </template>
        </ATable>

        <!--
          바우처 이력 (GET /system/logs/history/vouchers). 행 모양은 기관 이력 · Master 이력과 같습니다
          (docs/바우처_기관발행_관리자화면_가이드.md §2 이력 행). details 는 이벤트마다 키가 달라 들어온 키를 그대로 칩으로 펼치고,
          금액은 참고값입니다 — 정본은 기관 원장입니다.
        -->
        <ATable v-else :columns="voucherCols" :rows="current.items" row-key="idx">
          <template #cell:no="{ index }">
            <span class="hist-logs__num">{{ rowNo(index) }}</span>
          </template>
          <template #cell:at="{ row }">
            <span class="hist-logs__num">{{ fmtKst(row.at) }}</span>
          </template>
          <template #cell:organization="{ row }">
            <div class="hist-logs__stack">
              <span class="hist-logs__strong">{{ row.organization_name || '-' }}</span>
              <span v-if="row.organization_idx" class="hist-logs__sub hist-logs__num">#{{ row.organization_idx }}</span>
            </div>
          </template>
          <template #cell:event="{ row }">
            <APill :kind="historyEventKind(row.event)" :title="row.event">{{ historyEventLabel(row.event) }}</APill>
          </template>
          <template #cell:member="{ row }">
            <span v-if="row.member_users_idx" class="hist-logs__num">#{{ row.member_users_idx }}</span>
            <span v-else class="hist-logs__dash">—</span>
          </template>
          <template #cell:detail="{ row }">
            <div class="hist-logs__stack">
              <div v-if="historyChanges(row).length || historyDetailChips(row).length" class="hist-logs__chips">
                <span v-for="c in historyChanges(row)" :key="c.key" class="hist-logs__chip">
                  {{ c.key }} <b>{{ c.before }} → {{ c.after }}</b>
                </span>
                <span v-for="c in historyDetailChips(row)" :key="'d-' + c.key" class="hist-logs__chip">
                  {{ c.key }} <b>{{ c.value }}</b>
                </span>
              </div>
              <span v-if="row.reason" class="hist-logs__reason" :title="row.reason">{{ row.reason }}</span>
              <span v-if="!row.reason && !historyChanges(row).length && !historyDetailChips(row).length" class="hist-logs__dash">—</span>
            </div>
          </template>
          <template #cell:actor="{ row }">
            <div class="hist-logs__stack">
              <template v-if="row.actor">
                <div class="hist-logs__stack-top">
                  <span class="hist-logs__strong">{{ historyActorLabel(row.actor) }}</span>
                  <APill :kind="actorKind(row.actor)">{{ historyActorType(row.actor) }}</APill>
                </div>
                <span class="hist-logs__sub">{{ row.actor.email || '' }}</span>
              </template>
              <!-- 배치 · 시스템은 actor 가 null 입니다. -->
              <APill v-else kind="dark">시스템</APill>
            </div>
          </template>
        </ATable>

        <APagination
          :current-page="current.page"
          :total="current.total"
          :page-size="current.pageSize"
          @change="onPage"
        />
      </template>
    </ACard>

    <!-- 삭제 시점 수강생 명단 스냅샷 -->
    <AModal v-model="enrollModalOpen" title="삭제 시점 수강생 명단" size="lg">
      <div v-if="enrollTarget" class="hist-logs__modal">
        <div class="hist-logs__modal-sec">
          <h6 class="hist-logs__modal-h">
            <i class="material-icons-round hist-logs__sec-icon">meeting_room</i>
            클래스
          </h6>
          <p class="hist-logs__modal-line">
            {{ enrollTarget.classroom_name || '-' }}
            <span class="hist-logs__sub hist-logs__num">#{{ enrollTarget.classroom_idx }}</span>
            · 삭제 {{ fmtKst(enrollTarget.deleted_at) }}
          </p>
        </div>

        <div class="hist-logs__modal-sec">
          <h6 class="hist-logs__modal-h">
            <i class="material-icons-round hist-logs__sec-icon">group</i>
            명단 {{ (enrollTarget.enrollments || []).length }}명
          </h6>
          <!-- 승인 수강생이 있으면 클래스 삭제가 막히므로 status 는 request / rejection 만 옵니다. -->
          <ATable :columns="snapshotCols" :rows="enrollTarget.enrollments || []">
            <template #cell:name="{ row }">
              <div class="hist-logs__stack">
                <span class="hist-logs__strong">{{ row.name || '-' }}</span>
                <span class="hist-logs__sub">{{ row.email || '' }}</span>
              </div>
            </template>
            <template #cell:users_idx="{ row }">
              <span class="hist-logs__num">#{{ row.users_idx }}</span>
            </template>
            <template #cell:status="{ row }">
              <APill :kind="statusKind(row.status)">{{ statusLabel(row.status) }}</APill>
            </template>
            <template #cell:enrollment_date="{ row }">
              <span class="hist-logs__num">{{ row.enrollment_date || '-' }}</span>
            </template>
          </ATable>
        </div>
      </div>
      <template #foot>
        <ABtn kind="ghost" @click="enrollModalOpen = false">닫기</ABtn>
        <ABtn
          v-if="enrollTarget"
          kind="primary"
          icon="person_remove"
          @click="showEnrollmentDeletions(enrollTarget.classroom_idx)"
        >수강생 삭제 이력 보기</ABtn>
      </template>
    </AModal>
  </div>
</template>

<script>
import swal from "sweetalert2";
import APageHead from "@/components/Pineapple/APageHead.vue";
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AModal from "@/components/Pineapple/AModal.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import APagination from "@/components/Pineapple/APagination.vue";
import { apiErrorMessage } from "@/utils/apiError";
import { formatUtcToTimeZone, formatServerClock, NAIVE_AS_UNKNOWN } from "@/utils/datetime";
import { formatMoney } from "@/utils/currency";
import { formatPracticeType } from "@/utils/practiceType";
import { htmlToPlainText } from "@/utils/text";
import {
  HISTORY_EVENTS as VOUCHER_EVENTS,
  historyEventLabel, historyEventKind, historyChanges, historyDetailChips, historyActorLabel, historyActorType,
} from "@/utils/voucher";

const KST = "Asia/Seoul";

// 탭 정의. url 은 /system/logs/history/... 아래로 통일되어 있습니다.
const TABS = [
  {
    id: "refunds",
    label: "환불 이력",
    icon: "undo",
    url: "/system/logs/history/refunds",
    keywordHint: "학습자 · 실행자 이름/이메일, 결제코드",
  },
  {
    id: "classroomDeletions",
    label: "클래스 삭제",
    icon: "meeting_room",
    url: "/system/logs/history/classroom-deletions",
    keywordHint: "클래스명, 강사 · 수행자 이름/이메일",
  },
  {
    id: "enrollmentDeletions",
    label: "수강생 삭제",
    icon: "person_remove",
    url: "/system/logs/history/enrollment-deletions",
    keywordHint: "클래스명, 학생 · 강사 · 수행자 이름/이메일",
  },
  {
    id: "learningActivityDeletions",
    label: "학습활동 삭제",
    icon: "delete_sweep",
    url: "/system/logs/history/learning-activity-deletions",
    keywordHint: "학생 · 수행자 이름/이메일, 교재명, 클래스명, 사유",
  },
  {
    id: "vouchers",
    label: "바우처",
    icon: "confirmation_number",
    url: "/system/logs/history/vouchers",
    keywordHint: "기관명, 수행자 · 멤버 이름/이메일, 코드",
  },
];

// PaymentSalesList 와 같은 표를 씁니다. Stripe(S) 는 실제 결제가 없어 라벨을 두지 않고,
// 값이 오면 코드가 그대로 보이도록 폴백만 둡니다.
const PAYMENT_METHODS = [
  { value: "A", label: "Apple Pay" },
  { value: "G", label: "Google Pay" },
  { value: "T", label: "Toss" },
  { value: "L", label: "Lemonsqueezy" },
  { value: "F", label: "무료/사은품" },
  { value: "V", label: "기관 바우처" },
];

// source 는 값이 코드(toss_admin)와 한글 라벨("토스 웹훅")이 섞여 옵니다. 둘 다 받습니다.
const SOURCE_EXACT = {
  toss_admin: "토스 관리자 취소",
  ls_admin: "LemonSqueezy 관리자 취소",
  backfill: "이전 기록 이관",
  "토스 관리자 조회": "토스 즉시 반영",
  "토스 웹훅": "토스 웹훅",
  "LemonSqueezy 웹훅": "LemonSqueezy 웹훅",
};

const DELETED_COUNT_LABELS = {
  enrollment: "수강생",
  lecture_plan: "강의계획",
  bbs_notice: "공지",
  topic_subscription: "구독",
  classroom_textbook: "교재",
  exam_student: "시험 응시",
};

const ENROLLMENT_STATUS = {
  approval: { label: "승인", kind: "err" },
  request: { label: "신청 대기", kind: "warn" },
  rejection: { label: "반려", kind: "default" },
};

const blankFilters = () => ({
  startDate: "",
  endDate: "",
  keyword: "",
  paymentMethod: "",
  actorType: "all",
  classroomIdx: "",
  studentIdx: "",
  barcode: "",
  organizationIdx: "",
  event: "",
  memberUsersIdx: "",
});

const blankState = () => ({
  items: [],
  total: 0,
  page: 1,
  pageSize: 20,
  loading: false,
  loaded: false,
  filters: blankFilters(),
});

export default {
  name: "SystemLogsHistory",
  components: { APageHead, ACard, ABtn, APill, ATable, AModal, AEmpty, APagination },
  data() {
    return {
      TABS,
      PAYMENT_METHODS,
      VOUCHER_EVENTS,
      activeTab: "refunds",
      state: {
        refunds: blankState(),
        classroomDeletions: blankState(),
        enrollmentDeletions: blankState(),
        learningActivityDeletions: blankState(),
        vouchers: blankState(),
      },
      enrollModalOpen: false,
      enrollTarget: null,
      refundCols: [
        { key: "no", label: "번호", width: 60, align: "center" },
        { key: "refunded_at", label: "환불 시각 (KST)", width: 145 },
        { key: "payment", label: "결제", width: 165 },
        { key: "textbook", label: "교재", width: 200 },
        { key: "learner", label: "학습자", width: 170 },
        { key: "actor", label: "실행자", width: 180 },
        { key: "reason", label: "사유", width: 180 },
        { key: "amount", label: "환불액", width: 150, align: "right" },
        { key: "plans", label: "이용권", width: 100, align: "center" },
      ],
      classroomCols: [
        { key: "no", label: "번호", width: 60, align: "center" },
        { key: "deleted_at", label: "삭제 시각 (KST)", width: 145 },
        { key: "classroom", label: "클래스", width: 200 },
        { key: "instructor", label: "담당 강사", width: 180 },
        { key: "deleted_by", label: "수행자", width: 220 },
        { key: "enrollments", label: "수강생", width: 110, align: "center" },
        { key: "deleted_counts", label: "함께 삭제된 데이터", width: 260 },
        { key: "actions", label: "", width: 56, align: "center" },
      ],
      enrollmentCols: [
        { key: "no", label: "번호", width: 60, align: "center" },
        { key: "deleted_at", label: "삭제 시각 (KST)", width: 145 },
        { key: "classroom", label: "클래스", width: 190 },
        { key: "student", label: "학생", width: 180 },
        { key: "instructor", label: "담당 강사", width: 170 },
        { key: "status_before", label: "삭제 직전 상태", width: 120, align: "center" },
        { key: "enrollment_date", label: "신청일", width: 100 },
        { key: "deleted_by", label: "수행자", width: 220 },
      ],
      activityCols: [
        { key: "no", label: "번호", width: 60, align: "center" },
        { key: "deleted_at", label: "삭제 시각 (KST)", width: 145 },
        { key: "student", label: "학생", width: 170 },
        { key: "classrooms", label: "클래스", width: 170 },
        { key: "textbook", label: "교재", width: 190 },
        { key: "activity", label: "지운 기록", width: 260 },
        { key: "reason", label: "사유", width: 200 },
        { key: "deleted_by", label: "수행자", width: 200 },
      ],
      voucherCols: [
        { key: "no", label: "번호", width: 60, align: "center" },
        { key: "at", label: "시각 (KST)", width: 145 },
        { key: "organization", label: "기관", width: 190 },
        { key: "event", label: "이벤트", width: 150 },
        { key: "member", label: "대상 멤버", width: 110, align: "center" },
        { key: "detail", label: "내용", width: 320 },
        { key: "actor", label: "수행자", width: 200 },
      ],
      snapshotCols: [
        { key: "name", label: "학생", width: 220 },
        { key: "users_idx", label: "회원 번호", width: 100, align: "center" },
        { key: "status", label: "상태", width: 110, align: "center" },
        { key: "enrollment_date", label: "신청일", width: 110 },
      ],
    };
  },
  computed: {
    current() {
      return this.state[this.activeTab];
    },
    activeMeta() {
      return TABS.find((t) => t.id === this.activeTab) || TABS[0];
    },
  },
  created() {
    // 바우처 기관 상세의 「바우처 이력 전체 보기」에서 ?tab=vouchers&organizationIdx= 로 넘어옵니다.
    const q = this.$route.query;
    if (TABS.some((t) => t.id === q.tab)) this.activeTab = q.tab;
    if (q.organizationIdx && this.state[this.activeTab]) this.current.filters.organizationIdx = String(q.organizationIdx);
    this.fetchCurrent();
  },
  methods: {
    // ── 표시 헬퍼 ──
    fmtKst(iso) {
      if (!iso) return "-";
      const s = formatUtcToTimeZone(iso, KST);
      return s ? `${s.slice(0, 10)} ${s.slice(11, 16)}` : "-";
    },
    money(amount, code) {
      if (amount === null || amount === undefined) return "-";
      return formatMoney(amount, code);
    },
    paymentMethodLabel(code) {
      return PAYMENT_METHODS.find((m) => m.value === code)?.label || code || "-";
    },
    /**
     * 환불 금액 칸의 툴팁. LS 만 세전/실환불액이 갈리므로 그때만 붙입니다.
     * 다른 결제수단까지 붙이면 "다를 수 있다"가 상시 문구가 되어 진짜 LS 건에서 눈에 띄지 않습니다.
     */
    refundAmountTitle(row) {
      // null 이라야 Vue 가 속성을 지웁니다. "" 는 빈 title 속성이 그대로 붙습니다.
      if (row.payment_method !== "L") return null;
      return (
        "LemonSqueezy 는 구매자 국가의 세금을 더해 청구하고 환불 때 함께 돌려줍니다.\n" +
        "여기 금액은 세금을 뺀 결제 기록 금액 기준이라, 구매자가 실제로 돌려받은 금액은\n" +
        "LS 대시보드에서 확인해 주세요."
      );
    },
    refundSourceLabel(source) {
      if (!source) return "-";
      if (SOURCE_EXACT[source]) return SOURCE_EXACT[source];
      const vendor = source.startsWith("toss")
        ? "토스"
        : source.startsWith("google")
        ? "구글"
        : source.startsWith("apple")
        ? "애플"
        : source.startsWith("ls")
        ? "LemonSqueezy"
        : "";
      if (!vendor) return source;
      if (source.endsWith("_webhook")) return `${vendor} 웹훅`;
      if (source.endsWith("_batch")) return `${vendor} 배치`;
      if (source.endsWith("_sync")) return `${vendor} 즉시 반영`;
      return source;
    },
    // deleted_by.idx 는 emp 면 employee PK, user 면 users PK 라 네임스페이스가 다릅니다.
    // 화면에서도 account_type 을 함께 보여줘야 두 번호를 섞어 읽지 않습니다.
    actorLabel(actor) {
      if (!actor) return "-";
      return actor.account_type === "emp" ? "관리자" : "강사 본인";
    },
    actorKind(actor) {
      return actor?.account_type === "emp" ? "info" : "yellow";
    },
    statusLabel(status) {
      return ENROLLMENT_STATUS[status]?.label || status || "-";
    },
    statusKind(status) {
      return ENROLLMENT_STATUS[status]?.kind || "default";
    },
    deletedCountChips(counts) {
      if (!counts) return [];
      return Object.entries(counts)
        .filter(([, v]) => Number(v) > 0)
        .map(([key, value]) => ({ key, value, label: DELETED_COUNT_LABELS[key] || key }));
    },
    // ── 학습활동 삭제 이력 ──
    activityLesson(row) {
      const a = row.activity || {};
      return htmlToPlainText(a.chapterTitle) || (a.chapterNumber != null ? `레슨 ${a.chapterNumber}` : "-");
    },
    activityTypeLabel(row) {
      // practiceType 이 비어 있는 옛 기록은 학습활동 탭과 같이 문항풀이로 봅니다.
      return formatPracticeType(row.activity?.practiceType || "interaction");
    },
    // score 는 0~1, teacherScore 는 0~100 입니다.
    activityScore(row) {
      const a = row.activity || {};
      const parts = [];
      if (a.score !== null && a.score !== undefined) parts.push(`${Math.round(a.score * 100)}점`);
      if (a.teacherScore !== null && a.teacherScore !== undefined) parts.push(`강사 ${a.teacherScore}점`);
      return parts.join(" · ");
    },
    // 학습일시는 학습활동 시각 규칙을 따릅니다 — 오프셋이 있으면 KST 로, 표기가 없는 옛 값은 적힌 그대로
    // (docs/시각표기_시간대_규칙.md §3).
    activityTime(value) {
      return formatServerClock(value, { naiveZone: NAIVE_AS_UNKNOWN }) || "-";
    },
    historyEventLabel,
    historyEventKind,
    historyChanges,
    historyDetailChips,
    historyActorLabel,
    historyActorType,
    rowNo(index) {
      return (this.current.page - 1) * this.current.pageSize + index + 1;
    },

    // ── 조회 ──
    switchTab(tabId) {
      if (this.activeTab === tabId) return;
      this.activeTab = tabId;
      // 입력값은 탭마다 따로 두므로 탭 이동만으로 지워지지 않습니다.
      if (!this.current.loaded) this.fetchCurrent();
    },
    search() {
      this.current.page = 1;
      this.fetchCurrent();
    },
    resetFilters() {
      this.current.filters = blankFilters();
      this.search();
    },
    onPage(p) {
      this.current.page = p;
      this.fetchCurrent();
    },
    // 클래스 삭제 이력 → 같은 클래스의 수강생 삭제 이력으로 넘어갑니다.
    showEnrollmentDeletions(classroomIdx) {
      const target = this.state.enrollmentDeletions;
      target.filters = blankFilters();
      target.filters.classroomIdx = String(classroomIdx ?? "");
      target.page = 1;
      this.enrollModalOpen = false;
      this.activeTab = "enrollmentDeletions";
      this.fetchCurrent();
    },
    openEnrollments(row) {
      this.enrollTarget = row;
      this.enrollModalOpen = true;
    },
    buildParams() {
      const f = this.current.filters;
      const params = { page: this.current.page, pageSize: this.current.pageSize };
      if (f.startDate) params.startDate = f.startDate;
      if (f.endDate) params.endDate = f.endDate;
      if (f.keyword) params.keyword = f.keyword;
      if (this.activeTab === "refunds") {
        if (f.paymentMethod) params.paymentMethod = f.paymentMethod;
        // all 이 기본값이라 굳이 실어 보내지 않습니다.
        if (f.actorType && f.actorType !== "all") params.actorType = f.actorType;
      } else if (this.activeTab === "vouchers") {
        if (f.organizationIdx) params.organizationIdx = f.organizationIdx;
        if (f.event) params.event = f.event;
        if (f.memberUsersIdx) params.memberUsersIdx = f.memberUsersIdx;
      } else {
        if (f.classroomIdx) params.classroomIdx = f.classroomIdx;
        if (this.activeTab === "learningActivityDeletions") {
          if (f.studentIdx) params.studentIdx = f.studentIdx;
          if (f.barcode) params.barcode = f.barcode;
        }
      }
      return params;
    },
    async fetchCurrent() {
      const tabId = this.activeTab;
      const target = this.state[tabId];
      const f = target.filters;

      // 서버도 400 으로 막지만, 눌러 보기 전에 알려 주는 편이 낫습니다.
      if (f.startDate && f.endDate && f.startDate > f.endDate) {
        swal.fire("확인해 주세요", "시작일이 종료일보다 늦습니다.", "warning");
        return;
      }

      target.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: TABS.find((t) => t.id === tabId).url,
          params: this.buildParams(),
          loadingBar: true,
        });
        const body = res.data?.data || {};
        target.items = body.items || [];
        target.total = body.pagination?.totalCount || 0;
        // 서버가 되돌려준 페이지를 신뢰합니다. 없으면 요청한 페이지를 유지합니다.
        target.page = body.pagination?.currentPage || target.page;
        target.loaded = true;
      } catch (e) {
        console.error("이력 로그 조회 실패:", e);
        swal.fire("오류", apiErrorMessage(e, "이력을 불러오지 못했습니다."), "error");
        target.items = [];
        target.total = 0;
      } finally {
        target.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.hist-logs {
  display: flex;
  flex-direction: column;
  gap: 20px;

  &__tab-icon {
    font-size: 15px !important;
  }

  &__filters {
    display: grid;
    grid-template-columns: repeat(4, 1fr) auto;
    gap: 12px;
    align-items: end;

    @media (max-width: 1400px) { grid-template-columns: repeat(3, 1fr); }
    @media (max-width: 900px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 640px) { grid-template-columns: 1fr; }
  }

  &__filter--wide {
    grid-column: span 2;

    @media (max-width: 640px) { grid-column: span 1; }
  }

  &__filter-actions {
    display: flex;
    gap: 6px;
  }

  &__sec-title {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: $p-ink;
  }

  &__sec-icon {
    color: $p-point-deep;
    font-size: 16px !important;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    padding: 48px 24px;
    color: $p-ink-mid;
    font-size: 13px;
  }

  &__stack {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    &--end { align-items: flex-end; }
    &--center { align-items: center; }
  }

  &__stack-top {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
  }

  &__strong {
    font-weight: 700;
    color: $p-ink;
  }

  &__sub {
    font-size: 11px;
    color: $p-ink-soft;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__num {
    font-family: $p-font-mono;
    font-variant-numeric: tabular-nums;
    font-size: 11px;
  }

  &__reason {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: $p-ink-mid;
  }

  &__dash {
    color: $p-ink-softer;
  }

  &__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__chip {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 2px 7px;
    border-radius: 7px;
    background: $p-panel-alt;
    border: 1px solid $p-line;
    color: $p-ink-mid;
    font-size: 11px;

    > b {
      font-family: $p-font-mono;
      color: $p-ink;
    }
  }

  // 모달
  &__modal {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  &__modal-h {
    display: flex;
    align-items: center;
    gap: 6px;
    margin: 0 0 8px;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: $p-ink-mid;
  }

  &__modal-line {
    margin: 0;
    font-size: 13px;
    color: $p-ink;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }
}
</style>
